import { Link } from '@tanstack/react-router';
import { LinkIcon } from 'lucide-react';
import { type JSX } from 'react';

import { Button } from '@/components/ui/button.js';

/**
 * The READER has no SharePoint connection of their own (the browse and search endpoints answer 412). That
 * is not a failure to report — it is a step they have not taken yet, so it reads as an invitation and
 * links to where they make it.
 *
 * The wording used to be about the workspace — "SharePoint isn't connected to this workspace" — and after
 * personal connectors that is simply untrue: everybody connects under their own account, so a member with
 * no connection sees this while colleagues around them browse SharePoint all day. It also has to say what
 * they DO still have, because the answer surprises people: material a colleague put in the workspace
 * knowledge base stays searchable for everyone, connection or not.
 *
 * It lives on its own because both halves of a browsing surface reach it: the tree, and the search that
 * now sits above the tree. A 403 never comes here — that is a connection the caller may not use, which is
 * a refusal and not a missing step (see {@link isSharePointNotConnected}).
 */
export function SharePointNotConnectedNotice({
  workspaceId,
}: {
  workspaceId: string;
}): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border px-6 py-16 text-center">
      <span className="flex size-12 items-center justify-center rounded-2xl bg-primary/12 text-primary">
        <LinkIcon className="size-6" />
      </span>
      <div className="max-w-md space-y-3">
        <p className="font-heading text-base font-semibold">
          You haven't connected SharePoint yet
        </p>
        <p className="text-sm text-muted-foreground">
          Everybody connects SharePoint under their own account, so browsing
          sites, libraries and files here needs a connection of yours. Whatever
          your colleagues have added to the workspace knowledge base stays
          searchable without one.
        </p>
        <Button variant="outline" size="sm" asChild>
          <Link
            to="/workspaces/$id/manage"
            params={{ id: workspaceId }}
            search={{ tab: 'connectors' }}
          >
            Connect your SharePoint
          </Link>
        </Button>
      </div>
    </div>
  );
}
